<template>
  <div class="notification-container">
    <transition-group name="notification" tag="div">
      <div v-for="notification in notifications" :key="notification.id"
        :class="['notification', `notification-${notification.type}`]" @click="removeNotification(notification.id)">
        <div class="notification-content">
          <div class="notification-icon">
            <v-icon :color="getNotificationIconColor(notification.type)" size="20">
              {{ getNotificationIcon(notification.type) }}
            </v-icon>
          </div>
          <div class="notification-text">
            <div class="notification-title">{{ notification.title }}</div>
            <div class="notification-message">{{ notification.message }}</div>
            <div v-if="notification.details && notification.details.length > 0" class="notification-details">
              <ul>
                <li v-for="detail in notification.details" :key="detail">{{ detail }}</li>
              </ul>
            </div>
          </div>
          <div class="notification-close">
            <v-icon size="16" color="white">mdi-close</v-icon>
          </div>
        </div>
        <div class="notification-progress" :style="{ animationDuration: notification.duration + 'ms' }"></div>
      </div>
    </transition-group>
  </div>
</template>

<script setup>
import { useNotifications } from '@/composables/useNotification';

const {
  notifications,
  removeNotification,
  getNotificationIcon,
  getNotificationIconColor
} = useNotifications()
</script>

<style scoped>
/* Notification Container */
.notification-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  pointer-events: none;
}

/* Notification Styles */
.notification {
  pointer-events: all;
  margin-bottom: 12px;
  min-width: 320px;
  max-width: 480px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
}

.notification:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
}

.notification-success {
  background: rgb(var(--v-theme-success));
  color: white;
}

.notification-error {
  background: rgb(var(--v-theme-error));
  color: white;
}

.notification-warning {
  background: rgb(var(--v-theme-warning));
  color: white;
}

.notification-info {
  background: rgb(var(--v-theme-info));
  color: white;
}

.notification-content {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  gap: 12px;
}

.notification-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.notification-text {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  line-height: 1.3;
}

.notification-message {
  font-size: 13px;
  opacity: 0.9;
  line-height: 1.4;
  margin-bottom: 8px;
}

.notification-details {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  max-height: 220px;
  overflow-y: auto;
}

.notification-details ul {
  margin: 0;
  padding-left: 16px;
  list-style: none;
}

.notification-details li {
  font-size: 12px;
  opacity: 0.8;
  margin-bottom: 4px;
  line-height: 1.3;
}

.notification-details li:before {
  content: "•";
  margin-right: 8px;
  opacity: 0.6;
}

.notification-close {
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.notification:hover .notification-close {
  opacity: 1;
}

.notification-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  animation: progress linear forwards;
}

/* Animations */
.notification-enter-active {
  transition: all 0.3s ease;
}

.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-move {
  transition: transform 0.3s ease;
}

@keyframes progress {
  from {
    width: 100%;
  }

  to {
    width: 0%;
  }
}

/* Responsive Design */
@media (max-width: 600px) {
  .notification-container {
    top: 10px;
    right: 10px;
    left: 10px;
  }

  .notification {
    min-width: auto;
    max-width: none;
  }
}
</style>
